/**
 * Auth Status Component
 * Handles authentication state display in the navigation bar
 */

import { authModal, ensureAuthModalExists } from './auth-modal.js';
import { balanceService } from '../services/balance-service.js';
import { fpBadgeAnimator } from '../services/fp-badge-animator.js';

/**
 * User data returned from /api/user/init endpoint
 */
interface UserData {
  display_name?: string;
  username?: string;
  is_admin?: boolean;
  is_guest?: boolean;
  avatar_id?: string;
}

/**
 * Response from /api/user/init combined endpoint
 */
interface InitResponse {
  authenticated: boolean;
  is_guest?: boolean;
  user?: UserData;
  balance?: number;
  preferences?: UserPreferences;
  daily?: Record<string, unknown>;
}

/**
 * User preferences stored on window for FP toast checks
 */
interface UserPreferences {
  fp_toast_enabled?: boolean;
  [key: string]: unknown;
}

// Extend window interface
declare global {
  interface Window {
    __fortunioUserPrefs?: UserPreferences;
    updateAuthStatus: typeof initAuthStatus;
    updateFPBalance: typeof updateFPBalance;
    refreshFPBalance?: typeof refreshFPBalance;
  }
}

// DOM element references
const authUserEl = document.querySelector<HTMLElement>('#authUser');
const userDropdownMenuEl = document.querySelector<HTMLElement>('#userDropdownMenu');
const friendsNavItem = document.querySelector<HTMLElement>('#friendsNavItem');
const matchesNavItem = document.querySelector<HTMLElement>('#matchesNavItem');
const userDropdown = document.querySelector<HTMLElement>('#userDropdown');
const fpBalanceDisplay = document.querySelector<HTMLElement>('#fpBalanceDisplay');
const fpBalanceValue = document.querySelector<HTMLElement>('#fpBalanceValue');

/**
 * Fetch combined init data from /api/user/init
 * Replaces sequential /me → balance → preferences waterfall.
 */
async function fetchInit(): Promise<InitResponse> {
  try {
    const res = await fetch('/api/user/init', { credentials: 'same-origin' });
    const data: InitResponse = await res.json();
    return data;
  } catch {
    return { authenticated: false };
  }
}

/**
 * Update FP balance display in navbar
 */
function updateFPBalanceDisplay(balance: number, show: boolean): void {
  if (!fpBalanceDisplay || !fpBalanceValue) return;
  
  if (show && typeof balance === 'number' && !isNaN(balance)) {
    fpBalanceValue.textContent = balance.toLocaleString();
    fpBalanceDisplay.style.display = '';
  } else {
    fpBalanceDisplay.style.display = 'none';
  }
}

/**
 * Log out the current user
 */
async function logout(): Promise<void> {
  await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
  window.location.reload();
}

/**
 * Build the user dropdown menu based on auth state
 */
function buildDropdownMenu(authenticated: boolean, isGuest: boolean = false): void {
  if (!userDropdownMenuEl) return;
  
  userDropdownMenuEl.innerHTML = '';
  
  if (authenticated) {
    // Logged in user: Preferences, Logout (or Login for guests)
    const guestBadge = isGuest ? ' <span class="badge bg-secondary ms-1 small">Guest</span>' : '';
    const actionItem = isGuest
      ? `<li><a class="dropdown-item" href="#" id="guestLoginLink">Login</a></li>`
      : `<li><a class="dropdown-item text-danger" href="#" id="logoutLink">Logout</a></li>`;
    userDropdownMenuEl.innerHTML = `
      <li><a class="dropdown-item" href="/account">Preferences${guestBadge}</a></li>
      <li><hr class="dropdown-divider"></li>
      ${actionItem}
    `;

    if (isGuest) {
      const guestLoginLink = document.getElementById('guestLoginLink');
      if (guestLoginLink) {
        guestLoginLink.onclick = (e: Event) => {
          e.preventDefault();
          ensureAuthModalExists();
          authModal.show();
        };
      }
    } else {
      const logoutLink = document.getElementById('logoutLink');
      if (logoutLink) {
        logoutLink.onclick = (e: Event) => {
          e.preventDefault();
          logout();
        };
      }
    }
    
    // Show Friends link for authenticated users
    if (friendsNavItem) {
      friendsNavItem.style.display = '';
    }
    if (matchesNavItem) {
      matchesNavItem.style.display = '';
    }
  } else {
    // Guest: Show sign in/up option by clicking on username to open modal
    userDropdownMenuEl.innerHTML = `
      <li><a class="dropdown-item" href="#" id="openAuthModalLink">
        <i class="bi bi-gear me-2"></i>Sign in / Sign up
      </a></li>
    `;
    
    const openAuthModalLink = document.getElementById('openAuthModalLink');
    if (openAuthModalLink) {
      openAuthModalLink.onclick = (e: Event) => {
        e.preventDefault();
        ensureAuthModalExists();
        authModal.show();
      };
    }
    
    // Hide Friends link for guest users
    if (friendsNavItem) {
      friendsNavItem.style.display = 'none';
    }
    if (matchesNavItem) {
      matchesNavItem.style.display = 'none';
    }
  }
}

/**
 * Initialize auth status display
 */
async function initAuthStatus(): Promise<void> {
  if (!authUserEl || !userDropdownMenuEl) return;
  
  performance.mark('auth-status:start');

  // Initialize auth modal on page load (so it's available globally)
  ensureAuthModalExists();

  // Wire any [data-open-auth-modal] links on the page (e.g. homepage subtitle)
  document.querySelectorAll('[data-open-auth-modal]').forEach((el) => {
    el.addEventListener('click', (e: Event) => {
      e.preventDefault();
      authModal.show();
    });
  });
  
  const data = await fetchInit();
  performance.mark('auth-status:init-loaded');
  performance.measure('auth-status:init-latency', 'auth-status:start', 'auth-status:init-loaded');

  // Check if user is a guest (either not authenticated, or authenticated with is_guest flag)
  const isGuest = !data?.authenticated || data?.is_guest || data?.user?.is_guest;
  
  if (data?.authenticated) {
    // FIX-02: Show just "Guest" for guest users, not "Guest-XXXXX"
    if (isGuest) {
      authUserEl.textContent = 'Guest';
    } else if (data.user?.avatar_id) {
      authUserEl.innerHTML = `<img src="/api/avatars/${encodeURIComponent(data.user.avatar_id)}" alt="" class="nav-avatar" aria-hidden="true">`;
    } else {
      authUserEl.textContent = data.user?.display_name || data.user?.username || 'User';
    }
    buildDropdownMenu(true, isGuest);
    
    // Use combined init data for authenticated non-guest users
    if (!isGuest) {
      // FP balance (already in init response)
      const balance = data.balance ?? 0;
      balanceService.updateBalance(balance);
      updateFPBalanceDisplay(balance, true);
      fpBadgeAnimator.init(balance);
      
      // Preferences (already in init response)
      if (data.preferences) {
        window.__fortunioUserPrefs = data.preferences;
      } else {
        window.__fortunioUserPrefs = { fp_toast_enabled: true };
      }
    } else {
      // Hide FP balance for guest users
      updateFPBalanceDisplay(0, false);
    }
  } else {
    // FIX-02: Unauthenticated/anonymous sessions always show just "Guest"
    // (data.user.username contains auto-generated "Guest-XXXXX" which we must not show)
    authUserEl.textContent = 'Guest';
    buildDropdownMenu(false);
    
    // Hide FP balance for guests
    updateFPBalanceDisplay(0, false);
  }
  
  performance.mark('auth-status:complete');
  performance.measure('auth-status:total', 'auth-status:start', 'auth-status:complete');

  // Also make modal open when clicking the Guest name in dropdown
  if (userDropdown && !data?.authenticated) {
    userDropdown.addEventListener('click', (e: MouseEvent) => {
      // Check if this was a left-click (not right-click)
      if (e.button === 0) {
        // Allow the dropdown to open normally, then show auth modal
        setTimeout(() => {
          const authModalLink = document.getElementById('openAuthModalLink');
          // Guard: only show modal if the dropdown is actually open.
          // This distinguishes real user clicks (which open the dropdown) from
          // Bootstrap's programmatic focus-on-toggle after close (which does NOT
          // re-open the dropdown, so 'show' class is absent).
          const dropdownMenu = document.getElementById('userDropdownMenu');
          const dropdownIsOpen = dropdownMenu?.classList.contains('show') ?? false;
          if (authModalLink && !authModal.isVisible && dropdownIsOpen) {
            authModal.show();
          }
        }, 100);
      }
    });
  }
}

/**
 * Update FP balance in navbar after earning points
 * Can be called globally via window.updateFPBalance(newBalance)
 * Also updates the central BalanceService cache.
 */
function updateFPBalance(newBalance: number): void {
  balanceService.updateBalance(newBalance);  // Keep cache in sync
  updateFPBalanceDisplay(newBalance, true);
}

/**
 * Refresh FP balance by fetching from server
 * Use after earning points to show updated balance
 */
async function refreshFPBalance(): Promise<number | null> {
  const balance = await balanceService.refresh();
  if (typeof balance === 'number' && balance >= 0) {
    updateFPBalanceDisplay(balance, true);
    return balance;
  }
  // If fetch fails, hide badge
  updateFPBalanceDisplay(0, false);
  return null;
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', initAuthStatus);

// Export to window for global access
window.updateAuthStatus = initAuthStatus;
window.updateFPBalance = updateFPBalance;
window.refreshFPBalance = refreshFPBalance;

export { initAuthStatus, updateFPBalance, refreshFPBalance };
